/**
 * Заявка на генерацию: кнопка «запустить генерацию» в интерфейсе и очередь, из которой
 * сервис генерации забирает её ближайшим опросом.
 *
 * ПОЧЕМУ ЗАЯВКА, А НЕ ВЫЗОВ. Сервис генерации — отдельный процесс под отдельным
 * пользователем; HTTP-сервера у него нет, входящий доступ к нему запрещён таблицей
 * топологии, а наружу открыт только api.anthropic.com. Кнопка, которая «запускает
 * генерацию», физически не может его позвать; всё, что она может, — оставить запись,
 * которую тот прочитает сам. Это не обход требования, а его единственная честная форма:
 * очередь переживает перезапуск обоих процессов, а нажатие остаётся видимым в интерфейсе
 * до тех пор, пока из него не появится черновик.
 *
 * «НЕМЕДЛЕННО» ДАЁТ ЧАСТОТА ОПРОСА, А НЕ ВЫЗОВ. Сервис — демон и опрашивает очередь раз в
 * ~1500 мс (SMM_WRITER_POLL_MS), поэтому между нажатием и первым шагом прогона секунды.
 * Чтобы эти секунды не были для человека тишиной, сервис отчитывается по каждому этапу
 * (`GenerationProgressBodySchema`), а заявка отдаёт хвост его отчётов в интерфейс
 * (`GenerationRequestSchema.progress`).
 *
 * `format_hint`, а не `format`: выбор формата вместе с обоснованием принадлежит сервису
 * генерации (FR-1.3), а библиотека форматов лежит файлами в `knowledge/formats/` и
 * бэкенду недоступна — та же граница, из-за которой `content_plan_target.format_code`
 * остался кодом без словаря. Форма кода проверяется, словарь — нет.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';
import { codePointLen } from '../units.ts';
import { FORMAT_CODE_RE } from './content-plan.ts';
import { WorkFailureBodySchema, WorkItemViewSchema } from './work-items.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/**
 * Модели, которые кнопка предлагает выбрать для прогона. Уже, чем `GENERATION_MODELS`
 * (`./brand-config.ts`), и намеренно: там перечислены все модели, которым известна ЦЕНА,
 * включая те, которыми считает мониторинг. Здесь — только то, чем осмысленно писать пост.
 *
 * Все одинаковы по контракту запроса: adaptive thinking, уровни effort от low до max,
 * контекст 1M, вывод до 128K. Поэтому выбор модели НЕ требует пер-модельной сборки запроса —
 * транспорт остаётся одним. Первая в списке — значение по умолчанию для интерфейса.
 *
 * Opus 5.5 (22.09.2026) дешевле остальных — $4/$20 против $5/$25 — и отличается от Opus 5
 * тем, чего писатель не касается: мышление не выключается (транспорт его и не выключает),
 * принудительный `tool_choice` даёт 400 (инструментов нет), `effort` по умолчанию `medium`
 * (транспорт шлёт его явно из конфига). Но классификаторов отказа у него больше:
 * `reasoning_extraction` и `bio` к `cyber` — а отказы `reasoning_extraction` писатель уже
 * ловит на Opus 5.
 *
 * Порядок — от новой к старой: человек выбирает «взять помоложе или постарше», и список,
 * отсортированный иначе, читался бы как случайный.
 */
export const WRITER_SELECTABLE_MODELS = [
  'claude-opus-5-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
] as const;
export type WriterSelectableModel = (typeof WRITER_SELECTABLE_MODELS)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Модель писателя: настройка бренда, живущая в шапке рядом с рубильником публикаций

/**
 * Чем сервис генерации пишет ВСЁ у этого бренда: прогон по кнопке, сметающий проход по
 * темам расписания, переделки по правкам человека.
 *
 * ПОЧЕМУ ЭТО НЕ СВОЙСТВО ЗАЯВКИ. Тумблер в диалоге «Запустить генерацию» правил ровно один
 * прогон — тот, что человек в этот момент запускал. Остальное сервис писал моделью из
 * `config/brands/<brand>.yaml`, до которой интерфейс не дотягивается вовсе. «Выбрал 4.7, а
 * посты пишутся пятым» было не поломкой, а буквальным чтением кода; чтобы выбор значил то,
 * что человек в него вкладывает, он обязан жить рядом с брендом, а не с нажатием.
 *
 * `model: null` — отклонения нет, работает `generation.model` конфига бренда. Умолчание
 * интерфейс не назначает: конфига он не видит, и любая зашитая здесь модель молча
 * перебивала бы правку конфига.
 */
export const WriterModelOverrideSchema = z.strictObject({
  brand: z.string().min(1),
  model: z.string().nullable(),
  changed_at: IsoUtcMs.nullable(),
  changed_by_login: z.string().nullable(),
  note: z.string().nullable(),
  /**
   * Чем бренд пишет, когда отклонения нет. Бэкенд конфигов бренда не читает, поэтому здесь
   * `null` — законное значение и значит «умолчание известно только сервису». Поле нужно
   * шапке: без него подпись «как в бренде» не может назвать модель, и человек выбирает
   * вслепую между «как в бренде» и тремя именами.
   */
  brand_default: z.string().nullable(),
});
export type WriterModelOverride = z.output<typeof WriterModelOverrideSchema>;

export const SetWriterModelOverrideBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  /** `null` — снять отклонение и вернуться к модели конфига бренда. */
  model: z.enum(WRITER_SELECTABLE_MODELS).nullable(),
  /** Зачем сменили. Необязателен, но именно он объясняет, почему серия написана иначе. */
  note: z.string().min(1).max(500).nullable(),
});
export type SetWriterModelOverrideBody = z.output<typeof SetWriterModelOverrideBodySchema>;

/**
 * Ответ сервису генерации. Отдельная схема от человеческой по той же причине, что и у
 * заявки: сервису нужно значение, а не кто и когда его поставил. `model` — свободная
 * строка: бэкенд хранит текст, а какие модели сервис умеет звать — знание сервиса.
 */
export const WriterModelOverrideTaskSchema = z.strictObject({
  brand: z.string().min(1),
  model: z.string().nullable(),
});
export type WriterModelOverrideTask = z.output<typeof WriterModelOverrideTaskSchema>;

/**
 * Потолок свободной подсказки — 10 000 знаков (правка 19.09.2026, docs/release-2026-09-19.md
 * §7). Было 2000, как у `prompt` переделки; человеку этого не хватало: в подсказку кладут
 * выдержки, списки фактов, чужие формулировки «чего избегать», и 2000 обрывали мысль на
 * полуслове. Переделка свой потолок сохраняет — у неё другой жанр (замечание к готовому
 * тексту), и расти ему незачем.
 *
 * Число повторено CHECK'ами `generation_request.prompt` и `monitoring_request.prompt`
 * (migrations/0019) — значением, а не «примерно». Потолок `detail` отчёта сервиса этой
 * константой больше НЕ меряется: у него своя, `GENERATION_RESULT_DETAIL_MAX`, иначе
 * поднятие подсказки молча подняло бы потолок поля, которое зеркалит CHECK в 2000.
 */
export const GENERATION_PROMPT_MAX = 10_000;

/**
 * Потолок текста исхода, который сервис генерации присылает с результатом. Зеркало CHECK
 * `generation_request.outcome_detail` (≤ 2000).
 */
export const GENERATION_RESULT_DETAIL_MAX = 2000;

/**
 * Сколько заявок бренда может стоять в очереди (`queued`) одновременно. Держит бэкенд
 * (`domain/generation-requests.ts`); здесь — потому что число читают двое: интерфейсу
 * (полоса хода прогона, заказ статей из таблицы прогона) нужно знать, сколько живых заявок
 * может оказаться в списке, а модуль бэкенда ему недоступен (правило R4).
 */
export const GENERATION_QUEUE_MAX_PER_BRAND = 20;

/**
 * Сколько сервис генерации по умолчанию ждёт мониторинг своей заявки (`SMM_WRITER_MONITORING_WAIT_MS`
 * писателя, 12 мин). Бэкенд считает от него срок прогона мониторинга (`deadline_at` задания),
 * когда сервис не прислал своё ожидание во взятии (прежний сервис).
 */
export const GENERATION_MONITORING_WAIT_DEFAULT_MS = 720_000;

/**
 * Потолок КОНТЕКСТА — «свой пост словами» (правка 6 от 2026-09-08). Столько же, сколько
 * максимальный `source_markup` писателя (`api/writer.ts`): контекстом бывает готовый пост
 * целиком, и потолок обязан вмещать то, что из него получится.
 *
 * Подсказка и контекст — разные жанры и разные пути: подсказка СУЖАЕТ прогон по теме,
 * контекст темы не имеет вовсе и обход источников не запускает. Потолки у них поэтому свои
 * и расходятся осознанно (10 000 против 20 000).
 */
export const GENERATION_CONTEXT_MAX = 20_000;

/** Потолок ссылки-подсказки; CHECK таблицы совпадает с ним значением, а не «примерно». */
export const GENERATION_SOURCE_URL_MAX = 2000;

/**
 * Ссылка-подсказка принимается только http(s) и только с непустым хостом. Это НЕ политика
 * исходящих адресов поста (ПРАВИЛО C3-1) — та живёт на приёме версии черновика; здесь
 * отсекается `javascript:`, `file:` и прочее, чему в подсказке модели делать нечего.
 */
export const GENERATION_SOURCE_URL_RE = /^https?:\/\/[^\s/@]+\.[^\s/@]+(?:[/?#]\S*)?$/;

/**
 * Жизненный цикл заявки. `queued` → `taken` двигает сервис, `taken` → `done|failed` — он же,
 * `queued` → `cancelled` — человек. Отмены взятой заявки нет: сервис уже пишет по ней, и
 * «отменить» означало бы оставить его результат без места.
 */
export const GENERATION_REQUEST_STATES = [
  'queued',
  'taken',
  'done',
  'failed',
  'cancelled',
] as const;
export const GenerationRequestStateSchema = z.enum(GENERATION_REQUEST_STATES);
export type GenerationRequestState = z.output<typeof GenerationRequestStateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Прогресс прогона: что сервис генерации рассказывает о себе, пока идёт

/**
 * Словарь этапов прогона — общий на три стороны: сервис генерации его печатает, бэкенд
 * хранит (CHECK таблицы `generation_progress` повторяет этот список значениями), интерфейс
 * рисует по нему подпись в статус-баре слева от кнопки. Свободной строки здесь нет
 * намеренно: этап — ось, по которой человек понимает, где прогон стоит, и она не должна
 * меняться от того, как сервис назвал свою функцию сегодня.
 *
 * Порядок — порядок работы (docs/phase-2/03-generation.md §6.1 плюс бросок формы из
 * `docs/brand/1.4_zhivoy_tekst_de-AI.md`): заявка взята → выбрана тема → выбран формат →
 * брошена форма → написан текст → проверен гейтом живого текста → вынесен вердикт по
 * картинке → канонизирована разметка → отдан в intake. `done` и `failed` — не работа, а
 * исход прогона, и они всегда последние.
 *
 * ПОЧЕМУ `visual` СТОИТ МЕЖДУ `lint` И `canonicalize`, а не после канонизации, как
 * предлагал §4.1 плана. Вердикт — это два вызова модели, и они обязаны идти ВНУТРИ уже
 * открытой серии (ПРАВИЛО 2.6: между вызовами серии нет ввода-вывода, иначе 45k префикса
 * переписываются заново). Серию закрывает `series.end()` — `main.ts:518` и `:843`, — а
 * `progress.step('canonicalize', …)` стоит ниже, на `:864`. То есть по коду вердикт
 * физически случается ДО канонизации, и порядок в этом списке обязан это повторять:
 * `RunStatusBar.tsx:80` строит полосу прогресса именно из ПОРЯДКА этого массива.
 *
 * ПОЧЕМУ ЭТАП И СОСТОЯНИЕ — РАЗНЫЕ ПОЛЯ. `write` отчитывается дважды: `started`, когда
 * вызов ушёл в модель, и `ok`, когда ответ пришёл. Между ними десятки секунд, и всё это
 * время человеку надо показывать «пишет», а не пустоту. Склеенный код (`write_started`)
 * удваивал бы словарь на каждом новом этапе.
 */
export const GENERATION_STAGES = [
  'claimed',
  'topic',
  'format',
  'style_roll',
  'write',
  'lint',
  'visual',
  'canonicalize',
  'submit',
  'done',
  'failed',
] as const;
export const GenerationStageSchema = z.enum(GENERATION_STAGES);
export type GenerationStage = z.output<typeof GenerationStageSchema>;

/**
 * Состояние шага. `started` → `ok` | `failed`; четвёртого значения нет: прогон, который
 * оборвался вместе с процессом, не дописывает `failed` сам — его выдаёт незакрытый
 * `started` рядом с `state` заявки, и врать об этом строкой в журнале нельзя.
 */
export const GENERATION_STAGE_STATES = ['started', 'ok', 'failed'] as const;
export const GenerationStageStateSchema = z.enum(GENERATION_STAGE_STATES);
export type GenerationStageState = z.output<typeof GenerationStageStateSchema>;

/** Потолок детали в КОДПОИНТАХ — той же единице, которой меряет CHECK через length(). */
export const GENERATION_PROGRESS_DETAIL_MAX = 200;

/**
 * Деталь — одна строка и только одна: она рисуется в узком статус-баре шапки, где перевод
 * строки либо ломает верстку, либо молча теряется. Запрещены все управляющие символы, а не
 * один `\n`: `\r`, табуляция и escape-последовательности приезжают тем же путём (строка,
 * склеенная сервисом из вывода модели) и в однострочном поле означают то же самое.
 */
export const GENERATION_PROGRESS_DETAIL_RE = /^[^\p{Cc}]+$/u;

/**
 * Сколько шагов заявка отдаёт в интерфейс: ХВОСТ, а не журнал. Прогон укладывается в
 * десяток шагов, двадцать покрывают его с запасом на повторы этапа после ретрая; полная
 * история остаётся в таблице и достаётся запросом, а не грузом в каждом ответе списка.
 */
export const GENERATION_PROGRESS_MAX_STEPS = 20;

const ProgressDetail = z
  .string()
  .min(1)
  .regex(GENERATION_PROGRESS_DETAIL_RE, 'деталь — одна строка без управляющих символов')
  .refine((s) => codePointLen(s) <= GENERATION_PROGRESS_DETAIL_MAX, {
    error: `деталь длиннее ${String(GENERATION_PROGRESS_DETAIL_MAX)} кодпоинтов`,
  });

/**
 * Тело `POST /intake/v1/generation-requests/:id/progress` — «я на этом этапе». Оно же —
 * тело маршрута прогресса ПЕРЕДЕЛКИ (правка 11 от 2026-09-08): владелец строки прогресса
 * стоит в адресе (`:id` — заявка или переделка), а не в теле, поэтому второй схемы под
 * переделку не заводится. Словарь этапов тоже не расширяется: стадии переделки —
 * подмножество перечисленных выше.
 *
 * `detail` необязателен и свободен по содержанию: словарь этапов закрыт, а вот что именно
 * выпало броском формы или каким правилом отбил гейт — знает только сервис, и загонять это
 * в перечисление значило бы менять контракт на каждое новое правило линта.
 *
 * `at` необязателен, и это не забывчивость. Время шага знают часы сервиса, но порядок и
 * видимость шага определяет бэкенд: без `at` он ставит своё время приёма. Прислать своё
 * сервис может (шаг мог случиться до того, как связь восстановилась), заставлять его
 * держать синхронные часы ради статус-бара — нет.
 */
export const GenerationProgressBodySchema = z.strictObject({
  stage: GenerationStageSchema,
  state: GenerationStageStateSchema,
  detail: ProgressDetail.optional(),
  at: IsoUtcMs.optional(),
});
export type GenerationProgressBody = z.output<typeof GenerationProgressBodySchema>;

/**
 * Шаг в том виде, в каком его читает человек.
 *
 * `seq` — номер шага внутри ВЛАДЕЛЬЦА прогона, а не глобальный, и назначает его ПРИЁМНИК:
 * в теле (`GenerationProgressBodySchema`) поля `seq` нет вовсе, база ставит `max(seq)+1` по
 * владельцу внутри транзакции. Владельцев с 0017 два — заявка и переделка, — поэтому и
 * уникальность, и окно дедупликации ключуются по `coalesce(request_id, rework_id)`, а не по
 * одному `request_id`. Сама уникальность `(владелец, seq)` — защита от гонки двух
 * одновременных вставок, и только от неё: повтор доставки того же отчёта (сеть моргнула,
 * сервис повторил POST) она бы не поймала, потому что второй POST получил бы следующий
 * номер. Повтор склеивает окно по содержанию — совпали `stage`, `state`, `detail` и
 * разница `at` меньше секунды (`migrations/0017_context_rework_ops.sql`,
 * `domain/generation-requests.ts`).
 *
 * `detail` здесь уже не необязательное поле, а `null`: интерфейсу нужно одно правило
 * отрисовки, а не два.
 */
export const GenerationProgressStepSchema = z.strictObject({
  seq: z.int().min(1),
  stage: GenerationStageSchema,
  state: GenerationStageStateSchema,
  detail: ProgressDetail.nullable(),
  at: IsoUtcMs,
});
export type GenerationProgressStep = z.output<typeof GenerationProgressStepSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/generation-requests — человек нажал кнопку

/**
 * Все три содержательных поля необязательны, и это осознанно: «просто сгенерируй пост» —
 * законное намерение, ради которого кнопка и заведена. Пустая заявка означает «возьми
 * тему из своей очереди», подсказки её сужают.
 */
export const CreateGenerationRequestBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  format_hint: z.string().regex(FORMAT_CODE_RE).max(64).nullable(),
  prompt: z.string().min(1).max(GENERATION_PROMPT_MAX).nullable(),
  source_url: z.string().regex(GENERATION_SOURCE_URL_RE).max(GENERATION_SOURCE_URL_MAX).nullable(),
  /**
   * ЧЕТВЁРТЫЙ путь прогона, а не четвёртая подсказка: содержимое поля целиком уезжает
   * писателю, и тема берётся ИЗ НЕГО, а не из очереди мониторинга.
   *
   * `null` — обычный прогон. Непустая строка означает, что заявка идёт мимо мониторинга
   * вовсе: `createMonitoringRequest` для неё не вызывается, иначе писатель ждёт обхода
   * источников до двенадцати минут и потом сдаётся — темы всё равно нет.
   */
  context: z.string().min(1).max(GENERATION_CONTEXT_MAX).nullable(),
  /**
   * Модель этого прогона. `null` — «как в конфиге бренда»: тумблер задаёт отклонение от
   * умолчания, а не заменяет его. Так конфиг остаётся источником правды о том, чем канал
   * пишет обычно, а запись заявки — фактом о том, чем написан конкретный пост.
   */
  model: z.enum(WRITER_SELECTABLE_MODELS).nullable(),
  /**
   * Стиль прогона (docs/release-2026-09-19.md §5.4). `null` — стиль бренда по умолчанию на
   * момент прогона, ровно как `model`: заявка хранит ВЫБОР человека, а факт — ярлык стиля на
   * версии поста. Необязательно на входе (`default(null)`): прежние вызовы остаются
   * валидными, а на выходе поле есть всегда.
   */
  style_version_id: Uuid.nullable().default(null),
  /**
   * Второй стиль — прогон сравнения: по ОДНОЙ теме пишутся два черновика, второй — этим
   * стилем. `null` — обычный прогон, один пост. Совпадение с первым стилем (в том числе с
   * умолчанием) бэкенд отвергает: сравнивать стиль с самим собой — оплатить пост дважды.
   */
  compare_style_version_id: Uuid.nullable().default(null),
});
export type CreateGenerationRequestBody = z.output<typeof CreateGenerationRequestBodySchema>;

/**
 * Заявка в том виде, в каком её читает человек. `requested_by_login`, а не id: экран
 * показывает, кто нажал, а идентификатор человека ему не нужен ни для чего.
 */
export const GenerationRequestSchema = z.strictObject({
  request_id: Uuid,
  brand: z.string().min(1),
  format_hint: z.string().nullable(),
  prompt: z.string().nullable(),
  source_url: z.string().nullable(),
  /**
   * Исходный текст прогона по контексту, дословно. Едет обратно человеку, потому что
   * карточка заявки обязана показывать, ЧТО было отправлено: без этого «почему пост не
   * похож на мой текст» неразрешимо — сравнить не с чем.
   */
  context: z.string().nullable(),
  /** Что выбрал человек; `null` — модель бренда по умолчанию. */
  model: z.string().nullable(),
  /** Стиль, выбранный человеком, и его имя на момент ответа; `null` — умолчание бренда. */
  style_version_id: Uuid.nullable(),
  style_name: z.string().nullable(),
  /** Второй стиль прогона сравнения; `null` — обычный прогон. */
  compare_style_version_id: Uuid.nullable(),
  compare_style_name: z.string().nullable(),
  /** Черновик второго стиля; непусто только у сравнения, и только если он получился. */
  compare_draft_id: Uuid.nullable(),
  /** Статья из таблицы прогона мониторинга, по которой пишется пост (§3.4). */
  material_id: Uuid.nullable(),
  state: GenerationRequestStateSchema,
  requested_by_login: z.string().min(1),
  taken_by: z.string().nullable(),
  /** Непусто ⟺ state = 'done': черновик, который из заявки получился. */
  draft_id: Uuid.nullable(),
  outcome_detail: z.string().nullable(),
  created_at: IsoUtcMs,
  taken_at: IsoUtcMs.nullable(),
  finished_at: IsoUtcMs.nullable(),
  /**
   * Ход прогона: до GENERATION_PROGRESS_MAX_STEPS САМЫХ СВЕЖИХ шагов, по возрастанию
   * `seq`. Едет вместе с заявкой, а не отдельным маршрутом, по той же причине, что и
   * `known_formats`: экран рисует строку заявки и её статус-бар одним движением, и второй
   * запрос ради подписи под кнопкой был бы вторым источником рассинхрона. Пустой список —
   * законное состояние: заявка ещё в очереди, и рассказывать о ней пока нечего.
   */
  progress: z.array(GenerationProgressStepSchema).max(GENERATION_PROGRESS_MAX_STEPS),
  /**
   * Попытки и судьба заявки (P3, `api/work-items.ts`): номер попытки, когда следующая, почему
   * снята. `null` — записи работы нет (заявка закрыта до 0023). Необязательно на входе: экран
   * обязан пережить бэкенд старше поля.
   */
  work: WorkItemViewSchema.nullable().optional(),
});
export type GenerationRequest = z.output<typeof GenerationRequestSchema>;

/**
 * Ответ списка. `known_formats` едет рядом с заявками, а не отдельным маршрутом: экран
 * рисует список и форму подсказки одновременно, и второй запрос ради выпадающего списка
 * был бы вторым источником рассинхрона.
 *
 * Собран он ровно так же, как `known_formats` контент-плана, — из того, что у бренда уже
 * встречалось (цели плана и форматы прошлых постов). Библиотеки форматов у бэкенда нет,
 * и выдумывать её здесь нельзя; пустой список — честное «подсказывать пока нечем»,
 * и поле подсказки при этом остаётся свободным.
 */
export const GenerationRequestsResponseSchema = z.strictObject({
  requests: z.array(GenerationRequestSchema),
  known_formats: z.array(z.string()),
});
export type GenerationRequestsResponse = z.output<typeof GenerationRequestsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /intake/v1/generation-requests — сервис генерации забирает очередь

/**
 * Заявка в том виде, в каком её читает сервис. Ни логина, ни момента нажатия: сервису
 * нужно задание, а не кто и когда его оставил. Меньше полей — меньше поводов у сервиса
 * зависеть от того, что его не касается.
 */
export const GenerationTaskSchema = z.strictObject({
  request_id: Uuid,
  brand: z.string().min(1),
  format_hint: z.string().nullable(),
  prompt: z.string().nullable(),
  source_url: z.string().nullable(),
  /**
   * Непусто — сервис идёт третьим путём: тема не берётся из очереди и не ищется, а
   * СОЗДАЁТСЯ (`origin: 'manual'`) с этим текстом первичным материалом. Так сохраняются
   * карточка в бэклоге, аудит, цепочка версий и все инварианты intake.
   */
  context: z.string().nullable(),
  /**
   * Модель прогона или `null`. Сервис проверяет её своим перечислением заново: бэкенд
   * хранит строку, а какие модели сервис умеет звать — знание сервиса, не бэкенда.
   */
  model: z.string().nullable(),
  /**
   * Стиль прогона, выбранный человеком; `null` — сервис сам спрашивает умолчание бренда
   * (`GET /intake/v1/style-default`) на открытии прогона, как модель.
   */
  style_version_id: Uuid.nullable(),
  /**
   * Второй стиль: непусто — сервис пишет по той же теме второй черновик этим стилем и
   * отчитывается о нём полем `compare_draft_id` результата.
   */
  compare_style_version_id: Uuid.nullable(),
  created_at: IsoUtcMs,
});
export type GenerationTask = z.output<typeof GenerationTaskSchema>;

export const GenerationTasksResponseSchema = z.strictObject({
  tasks: z.array(GenerationTaskSchema),
});
export type GenerationTasksResponse = z.output<typeof GenerationTasksResponseSchema>;

/**
 * Тело `POST /intake/v1/generation-requests/:id/claim` — «взято». Пустое: кто взял, знает
 * токен, а когда — знают часы бэкенда. Поле «кто» в теле было бы полем, которому можно
 * соврать.
 */
export const ClaimGenerationRequestBodySchema = z.strictObject({
  /**
   * Сколько сервис будет ждать мониторинг этой заявки (P3). От взятия плюс это ожидание бэкенд
   * считает срок прогона мониторинга — его единственный потолок; без поля — умолчание
   * `GENERATION_MONITORING_WAIT_DEFAULT_MS`.
   */
  monitoring_wait_ms: z.int().min(60_000).max(3_600_000).optional(),
});
export type ClaimGenerationRequestBody = z.output<typeof ClaimGenerationRequestBodySchema>;

/**
 * Тело `POST /intake/v1/generation-requests/:id/result` — «выполнено» или «не вышло».
 *
 * `draft_id` обязателен при `done` и запрещён при `failed`: заявка, закрытая как
 * выполненная, обязана предъявить черновик — иначе человек в интерфейсе видит «готово»
 * и не находит поста. Проверку держит и схема, и CHECK таблицы.
 */
export const GenerationResultBodySchema = z
  .strictObject({
    outcome: z.enum(['done', 'failed']),
    draft_id: Uuid.nullable(),
    /**
     * Черновик второго стиля у прогона сравнения. Необязателен на входе: у обычного прогона
     * его нет, а у сравнения второй черновик может не получиться при готовом первом — тогда
     * заявка всё равно `done`, а причина — в `detail`.
     */
    compare_draft_id: Uuid.nullable().default(null),
    detail: z.string().max(GENERATION_RESULT_DETAIL_MAX).nullable(),
    /**
     * Вид отказа (P3) — только у `failed`. По нему бэкенд решает: вернуть заявку в очередь на
     * повтор (временный отказ, попытки остались) или закрыть. Без поля (прежний сервис) отказ
     * терминален, как до 0023.
     */
    failure: WorkFailureBodySchema.optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (
      v.compare_draft_id !== null &&
      (v.outcome !== 'done' || v.compare_draft_id === v.draft_id)
    ) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['compare_draft_id'],
        message: 'compare_draft_requires_distinct_done',
      });
    }
    if (v.outcome === 'done' && v.draft_id === null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['draft_id'],
        message: 'draft_id_required_for_done',
      });
    }
    if (v.outcome === 'done' && v.failure !== undefined) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['failure'],
        message: 'failure_forbidden_for_done',
      });
    }
    if (v.outcome === 'failed' && v.draft_id !== null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['draft_id'],
        message: 'draft_id_forbidden_for_failed',
      });
    }
  });
export type GenerationResultBody = z.output<typeof GenerationResultBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Переделка как полноценный прогон (правка 11 от 2026-09-08, §9.2)
//
// ЗАЧЕМ ЭТО ЗДЕСЬ, А НЕ В `api/ui.ts`. Полоса состояния наверху экрана рисует ОДНУ запись
// из двух очередей — заявок и переделок, — и выбирает её одним правилом. Значит обе
// стороны обязаны говорить об одном и том же: одни этапы (GENERATION_STAGES), одна форма
// шага (GenerationProgressStepSchema), один потолок хвоста. Разнеси их по двум файлам —
// и «переделка: пишем» разъедется с «пишем» ровно тогда, когда кто-то добавит этап.
//
// ПОЧЕМУ ПЕРЕДЕЛКА НЕ ЗАВОДИТ СТРОКУ ЗАЯВКИ (отвергнутая альтернатива). У заявок есть
// CHECK, связывающий состояние с черновиком, и потолок очереди на бренд, который переделки
// съедали бы; плюс переделки полезли бы в список «Последние заявки» так, будто кто-то
// нажимал кнопку. Поэтому владельцем строки прогресса стал `rework_id` (0017), а не
// поддельная заявка.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ЧТО именно переделывается — зеркало `rework_request.targets_json`. Список закрыт теми же
 * двумя значениями, что и на приёме решения (`domain/decisions.ts`, `normalizeTargets`):
 * порядок нормализован там, здесь он только проверяется.
 *
 * Нужен интерфейсу: заголовок полосы обязан сказать «переделка: пишем» против «переделка:
 * картинка», иначе человек не отличает переписывание текста от перерисовки карточки.
 */
export const REWORK_TARGETS = ['text', 'visual'] as const;
export const ReworkTargetSchema = z.enum(REWORK_TARGETS);
export type ReworkTarget = z.output<typeof ReworkTargetSchema>;

/**
 * Состояние строки переделки. Ровно два значения и ровно один переход `open → closed`
 * (`rework_request_state_guard`, 0001). Своего словаря состояний у переделки нет и не
 * заводится: `GENERATION_REQUEST_STATES` описывает жизнь ЗАЯВКИ (`taken`, `cancelled`), а
 * переделку никто не «берёт» отдельным действием и не отменяет.
 */
export const REWORK_RUN_STATES = ['open', 'closed'] as const;
export const ReworkRunStateSchema = z.enum(REWORK_RUN_STATES);
export type ReworkRunState = z.output<typeof ReworkRunStateSchema>;

/**
 * Переделка в том виде, в каком её читает ЧЕЛОВЕК: зеркало `GenerationRequestSchema`.
 * До этой правки очередь переделок лежала на слушателе intake и была доступна только по
 * служебному токену писателя — у интерфейса маршрута к ней не было вовсе, поэтому полоса
 * состояния честно не могла показать ничего.
 *
 * ТЕРМИНАЛЬНОГО ПОЛЯ ИСХОДА ЗДЕСЬ НЕТ, и это не пропуск. Переделка визуала может
 * закончиться вообще без новой версии («перерисовывать нечего»), и записать этот исход в
 * `rework_request` некуда: строка закрывается только версией (`CHECK (status = 'closed') =
 * (closed_by_version_id IS NOT NULL)`), а колонки исхода у неё нет. Отказ виден там, где он
 * и происходит, — последним шагом прогресса `{ stage: 'failed' }` с причиной в `detail`.
 * Заводить колонку ради второго дома того же факта значило бы чинить видимость схемой.
 */
export const ReworkRunSchema = z.strictObject({
  rework_id: Uuid,
  draft_id: Uuid,
  brand: z.string().min(1),
  /** Номер версии, С КОТОРОЙ переделка начата. Связь с версией идёт по номеру, не по id. */
  version_no: z.int().min(1),
  /** Непустой: `targets` без единого значения — это 400 на приёме решения. */
  targets: z.array(ReworkTargetSchema).min(1),
  state: ReworkRunStateSchema,
  /** Комментарий человека, породивший переделку. Он же — подпись под полосой состояния. */
  prompt: z.string().min(1),
  /** Кто отправил на переделку. Логин, а не id: экран показывает человека. */
  requested_by_login: z.string().min(1),
  /** Момент нажатия. По нему полоса выбирает активную запись из двух очередей. */
  created_at: IsoUtcMs,
  closed_at: IsoUtcMs.nullable(),
  /** Непусто ⟺ state = 'closed': версия, которой переделка закрылась. */
  closed_by_version_no: z.int().min(1).nullable(),
  /**
   * Тот же ХВОСТ, что у заявки, и тем же потолком: полоса состояния читает обе очереди
   * одним кодом, и разный потолок означал бы, что у переделки она обрывается раньше.
   */
  progress: z.array(GenerationProgressStepSchema).max(GENERATION_PROGRESS_MAX_STEPS),
  /** Попытки и судьба переделки (P3): отменена удалением черновика, снята отказом, ждёт повтора. */
  work: WorkItemViewSchema.nullable().optional(),
});
export type ReworkRun = z.output<typeof ReworkRunSchema>;

/**
 * Ответ человеческого маршрута очереди переделок. Обёрнут в объект, а не голый массив, —
 * как и очередь заявок: голый массив некуда расширять. Служебный `GET
 * /intake/v1/rework-requests` (его читает писатель) остаётся голым массивом и этой правкой
 * не трогается — у него другой потребитель и другой токен.
 */
export const ReworkRunsResponseSchema = z.strictObject({
  reworks: z.array(ReworkRunSchema),
});
export type ReworkRunsResponse = z.output<typeof ReworkRunsResponseSchema>;

/**
 * Вид прогона — РАЗЛИЧИТЕЛЬ, который интерфейс ставит САМ, слив две очереди в один список
 * кандидатов. На проводе его нет намеренно: добавить `kind` в тело заявки значило бы
 * тронуть схему, которую уже читают бэкенд, писатель и UI, ради поля, всегда равного
 * маршруту, откуда тело приехало.
 */
export const RUN_KINDS = ['generation', 'rework'] as const;
export type RunKind = (typeof RUN_KINDS)[number];
